import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Legal.module.scss';

const LAST_UPDATED = 'July 8, 2026';
const COMPANY_FULL = 'FOTH Operating System, Inc.';
const CONTACT_EMAIL = 'admin@wearefoth.com';
const APP_URL = 'https://www.wearefoth.com';

const Section = ({ id, title, children }) => (
  <section id={id} className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const { t } = useTranslation('legal');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tocItems = [
    ['#who-we-are', t('privacy.toc.who_we_are', '1. Who We Are')],
    ['#data-we-collect', t('privacy.toc.data_we_collect', '2. Data We Collect')],
    ['#instagram-api', t('privacy.toc.instagram_api', '3. Instagram / Facebook Data')],
    ['#how-we-use', t('privacy.toc.how_we_use', '4. How We Use Data')],
    ['#data-sharing', t('privacy.toc.data_sharing', '5. Data Sharing')],
    ['#data-retention', t('privacy.toc.data_retention', '6. Data Retention')],
    ['#your-rights', t('privacy.toc.your_rights', '7. Your Rights')],
    ['#security', t('privacy.toc.security', '8. Security')],
    ['#international', t('privacy.toc.international', '9. International Transfers')],
    ['#contact', t('privacy.toc.contact', '10. Contact Us')],
  ];

  return (
    <div className={styles.legalPage}>
      <div className={styles.legalHero}>
        <div className={styles.container}>
          <div className={styles.heroBadge}>{t('common.badge', 'Legal')}</div>
          <h1 className={styles.heroTitle}>{t('privacy.title', 'Privacy Policy')}</h1>
          <p className={styles.heroSub}>
            {t('common.last_updated', { date: LAST_UPDATED, defaultValue: `Last updated: ${LAST_UPDATED}` })}
          </p>
          <p className={styles.heroDesc}>
            {t('privacy.desc', `This Privacy Policy describes how ${COMPANY_FULL} ("FOTH", "we", "us", or "our") collects, uses, shares, and protects information about you when you use our platform.`)}
          </p>
        </div>
      </div>

      <div className={styles.legalLayout}>
        {/* Sidebar TOC */}
        <aside className={styles.toc}>
          <div className={styles.tocLabel}>{t('common.table_of_contents', 'Table of Contents')}</div>
          <nav>
            {tocItems.map(([href, label]) => (
              <a key={href} href={href} className={styles.tocLink}>{label}</a>
            ))}
          </nav>
          <div className={styles.tocActions}>
            <Link to="/data-deletion" className={styles.tocActionBtn}>
              {t('common.request_deletion', 'Request Data Deletion →')}
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <main className={styles.legalMain}>

          <Section id="who-we-are" title={t('privacy.sections.who_we_are.title', '1. Who We Are')}>
            <p>
              {t('privacy.sections.who_we_are.content', `${COMPANY_FULL} operates FOTH, a creator and brand operating system that centralizes collabs, applicants, reservations, and deliverables.`)}
            </p>
            <p>
              <strong>Contact:</strong>{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className={styles.inlineLink}>{CONTACT_EMAIL}</a>
            </p>
          </Section>

          <Section id="data-we-collect" title={t('privacy.sections.data_we_collect.title', '2. Data We Collect')}>
            <p>{t('privacy.sections.data_we_collect.intro', 'We collect information you provide directly to us, information collected automatically, and information from third-party integrations.')}</p>
            <ul className={styles.list}>
              <li>{t('privacy.sections.data_we_collect.account_info', 'Account & Profile Information: When you register, we collect your name, email address, password, role, phone number, and location.')}</li>
              <li>{t('privacy.sections.data_we_collect.brand_info', 'Brand / Business Information: Company name, business category, tax ID, and billing details processed by Stripe.')}</li>
              <li>{t('privacy.sections.data_we_collect.creator_info', 'Creator Information: Content categories, audience demographics via Meta API, portfolio links, and payout information.')}</li>
            </ul>
          </Section>

          <Section id="instagram-api" title={t('privacy.sections.instagram_api.title', '3. Instagram / Facebook Data (Meta API)')}>
            <div className={styles.highlightBox}>
              <p>
                {t('privacy.sections.instagram_api.intro', 'When you connect your Instagram Professional account via Meta OAuth, we access data in strict compliance with the Meta Platform Terms.')}
              </p>
            </div>

            <h3 className={styles.subTitle}>{t('privacy.sections.instagram_api.permissions_label', 'Permissions requested:')}</h3>
            <ul className={styles.list}>
              <li>{t('privacy.sections.instagram_api.perms.basic', 'instagram_basic: To read your Instagram account ID, username, profile picture, account type, and follower count.')}</li>
              <li>{t('privacy.sections.instagram_api.perms.insights', 'instagram_manage_insights: To read reach, impressions, engagement rates, and performance metrics on posts associated with active collaborations.')}</li>
              <li>{t('privacy.sections.instagram_api.perms.pages', 'pages_show_list & pages_read_engagement: To identify Facebook Pages linked to your Instagram professional account.')}</li>
            </ul>

            <h3 className={styles.subTitle}>{t('privacy.sections.instagram_api.guarantees_label', 'Our Commitments:')}</h3>
            <ul className={styles.list}>
              <li>{t('privacy.sections.instagram_api.no_selling', 'We never sell, rent, or monetize your Instagram data to data brokers or advertising networks.')}</li>
              <li>{t('privacy.sections.instagram_api.no_training', 'We do not use your Instagram content or metrics to train public AI models.')}</li>
              <li>{t('privacy.sections.instagram_api.minimal_scope', 'We access only data strictly necessary to fulfill collaboration contracts and report campaign analytics.')}</li>
              <li>{t('privacy.sections.instagram_api.user_control', 'You may revoke FOTH access to your Instagram account at any time.')}</li>
            </ul>
          </Section>

          <Section id="how-we-use" title={t('privacy.sections.how_we_use.title', '4. How We Use Data')}>
            <ul className={styles.list}>
              <li>{t('privacy.sections.how_we_use.purpose1', 'To provide, maintain, and improve platform functionality.')}</li>
              <li>{t('privacy.sections.how_we_use.purpose2', 'To calculate and display verified campaign performance metrics (reach, impressions, EMV).')}</li>
              <li>{t('privacy.sections.how_we_use.purpose3', 'To process payments, subscriptions, and creator payouts via Stripe Connect.')}</li>
              <li>{t('privacy.sections.how_we_use.purpose4', 'To detect, investigate, and prevent fraudulent transactions or fake accounts.')}</li>
              <li>{t('privacy.sections.how_we_use.purpose5', 'To comply with legal obligations, tax reporting, and anti-money laundering regulations.')}</li>
            </ul>
          </Section>

          <Section id="data-sharing" title={t('privacy.sections.data_sharing.title', '5. Data Sharing')}>
            <p>{t('privacy.sections.data_sharing.intro', 'We share your information only under limited circumstances:')}</p>
            <ul className={styles.list}>
              <li>{t('privacy.sections.data_sharing.with_counterparts', 'With Collaboration Counterparts: Brands see creator audience tiers and post performance metrics.')}</li>
              <li>{t('privacy.sections.data_sharing.service_providers', 'With Service Providers: Infrastructure and payment providers (Stripe, Cloudinary, hosting providers).')}</li>
              <li>{t('privacy.sections.data_sharing.legal', 'Legal Compliance: When required by subpoena, court order, or applicable law.')}</li>
            </ul>
          </Section>

          <Section id="data-retention" title={t('privacy.sections.data_retention.title', '6. Data Retention & Deletion')}>
            <p>{t('privacy.sections.data_retention.content', 'We retain personal data as long as your account remains active or as needed to provide our services. You can request deletion at any time.')}</p>
          </Section>

          <Section id="your-rights" title={t('privacy.sections.your_rights.title', '7. Your Rights')}>
            <p>{t('privacy.sections.your_rights.content', 'You have rights to access, rectify, delete, restrict processing, or export your personal data. Contact privacy@wearefoth.com.')}</p>
          </Section>

          <Section id="security" title={t('privacy.sections.security.title', '8. Security')}>
            <p>{t('privacy.sections.security.content', 'We implement industry-standard encryption in transit (TLS 1.3) and at rest (AES-256 Fernet key rotation for sensitive tokens).')}</p>
          </Section>

          <Section id="international" title={t('privacy.sections.international.title', '9. International Transfers')}>
            <p>{t('privacy.sections.international.content', 'Your data may be processed in servers located in the United States and Latin America under appropriate contractual safeguards.')}</p>
          </Section>

          <Section id="contact" title={t('privacy.sections.contact.title', '10. Contact Us')}>
            <p>{t('privacy.sections.contact.content', 'For privacy inquiries, contact our Data Protection Officer at privacy@wearefoth.com.')}</p>
          </Section>

        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
